import React, { forwardRef } from "react"
import ReactPhoneInput, { type Country } from "react-phone-number-input"
import "react-phone-number-input/style.css"
import { Control, FieldValues, useController } from "react-hook-form"
import {
  Box, FormControl, FormErrorMessage, FormLabel, Input, InputProps,
} from "@chakra-ui/react"

type Props<T extends FieldValues> = {
  control: Control<T, object>
  name: string
  placeholder?: string
  variant?: "outline" | "flushed" | "unstyled"
  error?: boolean
  errorMessage?: string
  label?: string
  labelWidth?: string | number
  defaultCountry?: Country
};

const PhoneNumberField = <T extends FieldValues, >({
  control,
  name,
  placeholder,
  variant,
  error,
  errorMessage,
  label,
  labelWidth,
  defaultCountry = "US",
}: Props<T>) => {
  const { field } = useController<any>({ name, control })

  let customInput
  switch (variant) {
    case "unstyled": {
      customInput = UnstyledInput
      break
    }
    case "flushed": {
      customInput = FlushedInput
      break
    }
    default: {
      customInput = OutlineInput
      break
    }
  }

  return (
    <FormControl
      display={label ? "flex" : undefined}
      alignItems={label ? "flex-start" : undefined}
      flexDirection={label ? "row" : undefined}
      isInvalid={error}
      mb={label ? 4 : undefined}
    >
      {label && (
        <FormLabel w={labelWidth ?? 1 / 4} fontSize="md" mt={2}>
          {label}
        </FormLabel>
      )}
      <Box w="100%">
        <ReactPhoneInput
          {...field}
          value={field.value || undefined}
          placeholder={placeholder}
          onChange={(value) => field.onChange(value)}
          inputComponent={customInput}
          international
          defaultCountry={defaultCountry}
        />
        {errorMessage && <FormErrorMessage ml={10}>{errorMessage}</FormErrorMessage>}
      </Box>
    </FormControl>
  )
}

const OutlineInput = forwardRef<HTMLInputElement, InputProps>((props, ref) => (
  <Input ref={ref} {...props} />
))

const FlushedInput = forwardRef<HTMLInputElement, InputProps>((props, ref) => (
  <Input ref={ref} {...props} variant="flushed" />
))

const UnstyledInput = forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(
  (props, ref) => <input ref={ref} {...props} />,
)

export default PhoneNumberField
